if (!customElements.get('oe-category-tiles')) {
  customElements.define('oe-category-tiles', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.track = this.querySelector('[data-category-track]');
      if (!this.track) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.cards = [...this.querySelectorAll('[data-category-card]')];
      this.dots = [...this.querySelectorAll('[data-category-dot]')];
      this.previous = this.querySelector('[data-category-previous]');
      this.next = this.querySelector('[data-category-next]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.near = new Set();
      this.visible = new Set();
      this.playRequests = new WeakMap();
      this.accessibleMedia = this.hasAttribute('data-accessible-media');
      this.userPlayback ??= new WeakMap();
      this.previous?.addEventListener('click', () => this.goTo(this.index - 1), { signal });
      this.next?.addEventListener('click', () => this.goTo(this.index + 1), { signal });
      this.querySelectorAll('[data-category-toggle]').forEach(button => button.addEventListener('click', () => {
        const card = button.closest('[data-category-card]');
        this.userPlayback.set(card, !this.shouldPlay(card));
        this.refreshVideos();
      }, { signal }));
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.goTo(index), { signal }));
      this.track.addEventListener('scroll', () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => { this.scrollFrame = 0; this.update(); });
      }, { passive: true, signal });
      this.addEventListener('keydown', event => {
        if (this.isGrid || event.altKey || event.ctrlKey || event.metaKey) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.goTo(this.index + (event.key === 'ArrowRight' ? 1 : -1));
        }
      }, { signal });
      this.addEventListener('shopify:block:select', event => {
        const card = event.target.closest('[data-category-card]');
        const index = this.cards.indexOf(card);
        if (index >= 0) {
          if (this.isGrid) card.scrollIntoView({ block: 'nearest', behavior: 'instant' });
          else this.goTo(index, true);
        }
      }, { signal });
      this.motion.addEventListener('change', () => this.refreshVideos(), { signal });
      document.addEventListener('visibilitychange', () => this.refreshVideos(), { signal });
      window.addEventListener('pageshow', () => this.refreshVideos(), { signal });
      this.resize = new ResizeObserver(() => {
        const index = this.index || 0;
        this.update();
        this.goTo(index, true);
      });
      this.resize.observe(this.track);
      this.update();
      this.dataset.carouselReady = '';
      this.observeVideos();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.resize?.disconnect();
      this.nearObserver?.disconnect();
      this.visibleObserver?.disconnect();
      cancelAnimationFrame(this.scrollFrame);
      this.cards?.forEach(card => this.unloadVideo(card));
    }

    update() {
      this.isGrid = getComputedStyle(this.track).display === 'grid';
      const columns = Number(getComputedStyle(this).getPropertyValue('--oe-category-columns')) || 1;
      this.lastIndex = this.isGrid ? 0 : Math.max(0, this.cards.length - columns);
      const step = this.cards[1] ? this.cards[1].offsetLeft - this.cards[0].offsetLeft : this.track.clientWidth;
      this.index = Math.max(0, Math.min(this.lastIndex, Math.round(this.track.scrollLeft / (step || 1))));
      if (this.previous) this.previous.disabled = this.index === 0;
      if (this.next) this.next.disabled = this.index === this.lastIndex;
      this.toggleAttribute('data-single-page', this.lastIndex === 0);
      this.dots.forEach((dot, index) => {
        dot.hidden = index > this.lastIndex;
        if (index === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    goTo(index, immediately = false) {
      if (this.isGrid) return;
      const target = this.cards[Math.max(0, Math.min(this.lastIndex, index))];
      if (target) this.track.scrollTo({ left: target.offsetLeft, behavior: immediately || this.motion.matches ? 'instant' : 'smooth' });
    }

    observeVideos() {
      const videoCards = this.cards.filter(card => card.querySelector('[data-category-video]'));
      if (!videoCards.length || !('IntersectionObserver' in window)) return;
      this.nearObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.near.add(entry.target);
          else this.near.delete(entry.target);
        }
        this.refreshVideos();
      }, { rootMargin: '250px 0px' });
      this.visibleObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.visible.add(entry.target);
          else this.visible.delete(entry.target);
        }
        this.refreshVideos();
      }, { threshold: 0 });
      videoCards.forEach(card => { this.nearObserver.observe(card); this.visibleObserver.observe(card); });
    }

    shouldPlay(card) {
      return !this.accessibleMedia || (this.userPlayback.has(card) ? this.userPlayback.get(card) : !this.motion.matches);
    }

    refreshVideos() {
      this.cards.forEach(card => {
        if (this.accessibleMedia) {
          const button = card.querySelector('[data-category-toggle]');
          const title = card.querySelector('.oe-category-tiles__title')?.textContent.trim() || '';
          if (button) {
            button.classList.toggle('is-paused', !this.shouldPlay(card));
            button.setAttribute('aria-label', `Animation ${this.shouldPlay(card) ? 'pausieren' : 'abspielen'}: ${title}`);
          }
          if (!this.shouldPlay(card)) {
            const video = card.querySelector('video');
            if (video) { video.autoplay = false; video.pause(); }
            card.removeAttribute('data-video-playing');
            return;
          }
        }
        if (!this.near.has(card)) {
          // Retain the loaded source so returning to a tile does not download it again.
          const loadedVideo = card.querySelector('video');
          if (loadedVideo) {
            loadedVideo.autoplay = false;
            loadedVideo.pause();
            card.removeAttribute('data-video-playing');
          }
          return;
        }
        const video = this.loadVideo(card);
        if (!video) return;
        if (!document.hidden && this.visible.has(card)) {
          this.playVideo(card, video);
        } else {
          video.autoplay = false;
          video.pause();
          card.removeAttribute('data-video-playing');
        }
      });
    }

    playVideo(card, video) {
      if (!this.isConnected || !video.isConnected || document.hidden || !this.near.has(card) || !this.visible.has(card) || !this.shouldPlay(card)) return;
      video.autoplay = true;
      video.muted = true;
      video.defaultMuted = true;
      if (!video.paused) {
        if (video.readyState >= 2) card.setAttribute('data-video-playing', '');
        return;
      }
      if (this.playRequests.has(video)) return;
      const request = video.play();
      this.playRequests.set(video, request);
      request.then(() => {
        if (!this.shouldPlay(card) || document.hidden || !this.visible.has(card)) { video.pause(); card.removeAttribute('data-video-playing'); return; }
        if (video.isConnected && !video.paused) card.setAttribute('data-video-playing', '');
      }).catch(() => {
        // An older interrupted request must not hide a video that already started.
        if (card.querySelector('video') === video && video.paused) card.removeAttribute('data-video-playing');
      }).finally(() => this.playRequests.delete(video));
    }

    loadVideo(card) {
      if (card.dataset.videoFailed) return null;
      const existing = card.querySelector('video');
      if (existing) return existing;
      const template = card.querySelector('[data-category-video]');
      if (!template) return null;
      const video = template.content.querySelector('video').cloneNode(true);
      const sources = [...video.querySelectorAll('source[data-src]')].sort((a, b) => Number(a.dataset.width) - Number(b.dataset.width));
      const requiredWidth = card.clientWidth * Math.min(devicePixelRatio || 1, matchMedia('(max-width: 767px)').matches ? 1.5 : 2);
      const source = sources.find(item => Number(item.dataset.width) >= requiredWidth) || sources.at(-1);
      if (!source) return null;
      sources.forEach(item => { if (item !== source) item.remove(); });
      source.src = source.dataset.src;
      video.muted = true;
      video.defaultMuted = true;
      video.autoplay = false;
      video.preload = 'metadata';
      video.addEventListener('playing', () => {
        if (video.isConnected) card.setAttribute('data-video-playing', '');
      });
      // Retry after media becomes ready or another theme component pauses all media.
      const resume = () => this.playVideo(card, video);
      video.addEventListener('canplay', resume);
      video.addEventListener('pause', resume);
      const failed = () => {
        if (!video.isConnected) return;
        card.dataset.videoFailed = 'true';
        card.removeAttribute('data-video-playing');
      };
      video.addEventListener('error', failed);
      source.addEventListener('error', failed);
      card.querySelector('[data-category-media]').append(video);
      video.load();
      return video;
    }

    unloadVideo(card) {
      const video = card.querySelector('video');
      card.removeAttribute('data-video-playing');
      if (!video) return;
      video.pause();
      video.removeAttribute('src');
      video.querySelectorAll('source').forEach(source => source.removeAttribute('src'));
      video.load();
      video.remove();
    }
  });
}
